import { useMemo } from 'react';
import Select from 'react-select';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import tw from 'tailwind-styled-components';

interface PaginationProps {
  page: number;
  count: number;
  rowsPerPage: number;
  setPage: (arg: number) => void;
}

const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    border: '2px solid #e2e8f0',
    borderRadius: '0.375rem',
    padding: '0.1rem',
    minHeight: '36px',
    minWidth: '120px',
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '0.375rem',
  }),
};

const Pagination = ({ page, count, rowsPerPage, setPage }: PaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));

  const pageOptions = useMemo(
    () =>
      Array.from({ length: totalPages }, (_, i) => ({
        value: i + 1,
        label: `หน้า ${i + 1}`,
      })),
    [totalPages],
  );

  const selectedPage =
    pageOptions.find((option) => option.value === page) ?? pageOptions[0];

  const getPages = () => {
    const pages = [];
    if (totalPages <= 7) {
      // If there are 7 or fewer pages, show them all
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show the first two pages, the last two pages, and the pages around the current page
      pages.push(1);
      if (page > 4) pages.push('...');
      for (
        let i = Math.max(2, page - 2);
        i <= Math.min(totalPages - 1, page + 2);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 3) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <nav aria-label="page navigation">
        <ul className="list-style-none flex justify-center gap-1">
          <li>
            <Button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              <FaChevronLeft fontSize={20} className="text-primary" />
            </Button>
          </li>
          {getPages().map((pageNum: any, index: number) => (
            <li key={index}>
              {pageNum === '...' ? (
                <span className="mx-2">...</span>
              ) : (
                <A
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage(pageNum);
                  }}
                  className={`${page === pageNum && 'bg-primary text-white'}`}
                >
                  {pageNum}
                </A>
              )}
            </li>
          ))}
          <li>
            <Button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages || count === 0}
            >
              <FaChevronRight fontSize={20} className="text-primary" />
            </Button>
          </li>
        </ul>
      </nav>

      {count > 0 && totalPages > 1 && (
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-sm text-neutral-600 z-30">
            ไปที่หน้า
          </span>
          <Select
            value={selectedPage}
            options={pageOptions}
            onChange={(option) => {
              if (option) setPage(option.value);
            }}
            styles={selectStyles}
            isSearchable
            menuPlacement="top"
            placeholder="ค้นหาหน้า..."
            noOptionsMessage={() => 'ไม่พบหน้า'}
            filterOption={(option, inputValue) =>
              option.label
                .toLowerCase()
                .includes(inputValue.toLowerCase()) ||
              String(option.value).includes(inputValue)
            }
            className="min-w-[140px]"
          />
        </div>
      )}
    </div>
  );
};

const A = tw.a`relative block rounded bg-transparent px-3 py-1.5 text-sm text-neutral-600 transition-all duration-300 hover:bg-neutral-100`;

const Button = tw.button`relative block rounded bg-transparent px-3 py-1.5 text-sm text-neutral-600 transition-all duration-300 hover:bg-neutral-100`;

export default Pagination;
